import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { frDate } from "@/lib/date";
import { CommuneAutocomplete } from "@/components/CommuneAutocomplete";
import { ChevronRight, Search } from "lucide-react";

export default function Recherche() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const onSelect = async (c) => {
    if (!c) {
      setData(null);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const r = await axios.get(`${API}/communes/${c.code_insee}/arretes`);
      setData(r.data);
    } catch (e) {
      setData({ commune: c.commune, departement: c.departement, code_insee: c.code_insee, arretes: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="screen-recherche">
      <h1 style={{ fontSize: "1.7rem", margin: "0.2rem 0 0.5rem" }}>Rechercher une commune</h1>
      <p style={{ color: "var(--muted)", marginTop: 0 }}>
        Consultez les arrêtés de reconnaissance de catastrophe naturelle déjà publiés pour une commune,
        y compris ceux antérieurs à une inscription (ils ne déclenchent pas de notification).
      </p>

      <div style={{ marginTop: "1rem" }}>
        <CommuneAutocomplete onSelect={onSelect} testId="recherche-commune" />
      </div>

      {loading && <p style={{ color: "var(--muted)", marginTop: "1.2rem" }}>Recherche…</p>}

      {!loading && data && (
        <div style={{ marginTop: "1.4rem" }} data-testid="recherche-results">
          <h2 style={{ fontSize: "1.15rem" }}>
            {data.commune} <span style={{ color: "var(--muted)", fontWeight: 400 }}>· {data.departement}</span>
          </h2>
          {data.arretes.length === 0 ? (
            <p data-testid="recherche-empty" style={{ color: "var(--muted)" }}>
              Aucun arrêté de reconnaissance trouvé pour cette commune dans notre base.
            </p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "0.8rem" }}>
              {data.arretes.map((a) => (
                <li key={a.cid}>
                  <button
                    type="button"
                    data-testid={`arrete-card-${a.cid}`}
                    onClick={() => navigate(`/arrete/${a.cid}/${data.code_insee}`)}
                    className="pc-card"
                    style={{ width: "100%", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.8rem" }}
                  >
                    <span style={{ display: "grid", gap: "0.35rem" }}>
                      <span style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        {a.phenomenes.map((p, i) => (
                          <span key={i} style={{ fontWeight: 600 }}>{p.phenomene}{i < a.phenomenes.length - 1 ? "," : ""}</span>
                        ))}
                      </span>
                      <span style={{ fontSize: "0.88rem", color: "var(--muted)" }}>
                        Publié le {frDate(a.date_publication)} · date limite {frDate(a.date_limite)}
                      </span>
                      <span className={`pc-badge ${a.statut_delai}`} data-testid={`arrete-statut-${a.cid}`}>
                        {a.statut_delai === "en_cours" ? "Délai en cours" : "Délai échu"}
                      </span>
                    </span>
                    <ChevronRight size={20} color="var(--muted)" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && !searched && (
        <div style={{ marginTop: "1.6rem", display: "flex", gap: "0.55rem", alignItems: "center", color: "var(--muted)" }}>
          <Search size={18} />
          <span>Commencez à taper le nom d'une commune ou un code INSEE.</span>
        </div>
      )}
    </div>
  );
}
